import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ChevronRight, MessageCircle, Phone, Camera } from 'lucide-react';
import type { ServicePageContent } from '../seo/service-pages';
import { AboutStudioSection } from './AboutStudioSection';
import { useSettings } from '../hooks/useSettings';
import { serviceOffersService, ServiceOffer } from '../services/serviceOffers';

type Props = {
  content: ServicePageContent;
  /** Override bottom CTA portfolio link (e.g. `/portfolio?category=…`). */
  portfolioCtaHref?: string;
};

const RELATED_BY_SLUG: Record<string, { to: string; label: string }[]> = {
  services: [
    { to: '/mariage', label: 'Mariage' },
    { to: '/photobooth', label: 'Photobooth' },
    { to: '/production', label: 'Production vidéo' },
  ],
  mariage: [
    { to: '/photographe-mariage-djerba', label: 'Mariage Djerba' },
    { to: '/portfolio/mariage', label: 'Portfolio mariage' },
  ],
  photobooth: [
    { to: '/photobooth-tunisie', label: 'Photobooth Tunisie' },
    { to: '/photobooth-djerba', label: 'Photobooth Djerba' },
    { to: '/portfolio/photobooth', label: 'Portfolio photobooth' },
  ],
  production: [
    { to: '/video-corporate-tunisie', label: 'Corporate Tunisie' },
    { to: '/photographe-evenement-tunis', label: 'Événement Tunis' },
    { to: '/portfolio/corporate', label: 'Portfolio corporate' },
  ],
  'photographe-mariage-djerba': [
    { to: '/mariage', label: 'Mariage photo & vidéo' },
    { to: '/photobooth-djerba', label: 'Photobooth Djerba' },
    { to: '/portfolio/mariage', label: 'Réalisations mariage' },
  ],
  'photographe-evenement-tunis': [
    { to: '/production', label: 'Production vidéo' },
    { to: '/video-corporate-tunisie', label: 'Vidéo corporate' },
    { to: '/portfolio/corporate', label: 'Portfolio corporate' },
  ],
  'photobooth-djerba': [
    { to: '/photobooth', label: 'Photobooth Tunisie' },
    { to: '/photographe-mariage-djerba', label: 'Mariage Djerba' },
    { to: '/mariage', label: 'Offre mariage' },
  ],
  'video-corporate-tunisie': [
    { to: '/production', label: 'Production vidéo' },
    { to: '/photographe-evenement-tunis', label: 'Photo événement Tunis' },
    { to: '/portfolio/corporate', label: 'Portfolio corporate' },
  ],
  'portfolio-mariage': [
    { to: '/mariage', label: 'Services mariage' },
    { to: '/photographe-mariage-djerba', label: 'Djerba' },
    { to: '/contact', label: 'Devis gratuit' },
  ],
  'portfolio-photobooth': [
    { to: '/photobooth', label: 'Location photobooth' },
    { to: '/photobooth-djerba', label: 'Djerba' },
    { to: '/contact', label: 'Devis gratuit' },
  ],
  'portfolio-corporate': [
    { to: '/production', label: 'Production' },
    { to: '/video-corporate-tunisie', label: 'Corporate Tunisie' },
    { to: '/contact', label: 'Devis gratuit' },
  ],
  'portfolio-shooting': [
    { to: '/services', label: 'Tous les services' },
    { to: '/portfolio', label: 'Portfolio complet' },
    { to: '/contact', label: 'Devis gratuit' },
  ],
  'photobooth-tunisie': [
    { to: '/photobooth', label: 'Photobooth détail' },
    { to: '/photobooth-djerba', label: 'Photobooth Djerba' },
    { to: '/mariage', label: 'Mariage' },
  ],
};

const HERO_IMAGE_BY_SLUG: Record<string, string> = {
  'photographe-mariage-djerba': '/showcase-hero.jpg',
  'photobooth-djerba': '/showcase-hero.jpg',
  'photographe-evenement-tunis': '/studio-hero.jpg',
  'video-corporate-tunisie': '/studio-hero.jpg',
  mariage: '/showcase-hero.jpg',
  photobooth: '/showcase-hero.jpg',
  production: '/studio-hero.jpg',
  services: '/studio-hero.jpg',
  'photobooth-tunisie': '/showcase-hero.jpg',
};

export function ServiceSeoLanding({ content, portfolioCtaHref }: Props) {
  const { settings, loading: settingsLoading } = useSettings();
  const [offers, setOffers] = useState<ServiceOffer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(true);

  useEffect(() => {
    serviceOffersService.getAll()
      .then(data => setOffers(data.filter((o: ServiceOffer) => o.isActive)))
      .catch(console.error)
      .finally(() => setLoadingOffers(false));
  }, []);

  const heroImage = HERO_IMAGE_BY_SLUG[content.slug] ?? '/studio-hero.jpg';
  
  // Filtrage intelligent des offres basées sur le slug de la page SEO
  const filteredOffers = offers.filter(o => {
    const slug = content.slug.toLowerCase();
    if (slug === 'services') return true; // Tout afficher sur la page générale
    
    const title = o.title.toLowerCase();
    const desc = (o.description || '').toLowerCase();
    
    // Mots-clés par catégorie
    if (slug.includes('mariage') && (title.includes('mariage') || desc.includes('mariage') || title.includes('wedding'))) return true;
    if (slug.includes('photobooth') && (title.includes('photobooth') || desc.includes('photobooth') || title.includes('borne'))) return true;
    if (slug.includes('production') || slug.includes('corporate') || slug.includes('video')) {
       if (title.includes('vidéo') || desc.includes('vidéo') || title.includes('corporate') || title.includes('film')) return true;
    }
    if (slug.includes('impression') || slug.includes('cadeau') || slug.includes('album') || slug.includes('cadre')) {
       if (title.includes('impression') || title.includes('album') || title.includes('cadre') || title.includes('tirage')) return true;
    }
    
    return slug.includes(o.category?.slug || 'none');
  });
  const related = RELATED_BY_SLUG[content.slug] ?? [
    { to: '/services', label: 'Services' },
    { to: '/mariage', label: 'Mariage' },
    { to: '/contact', label: 'Contact' },
  ];

  const phone = settings?.contact_phone?.trim();
  const wa =
    phone &&
    `https://wa.me/${phone.replace(/[\s-+]/g, '')}?text=${encodeURIComponent("Bonjour Lbenna Prod ! Je voudrais un devis.")}`;

  return (
    <article className="min-h-screen bg-white">
      <header className="relative overflow-hidden bg-gray-950 pt-28 pb-24 text-white md:pt-32 md:pb-28">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/50 via-gray-950/90 to-gray-950" />

        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
          <nav aria-label="Fil d'Ariane" className="mb-8 flex flex-wrap items-center justify-center gap-1 text-xs font-bold text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">
              Accueil
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            <Link to="/services" className="hover:text-white transition-colors">
              Services
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            <span className="text-primary-300">{content.h1}</span>
          </nav>

          <div className="text-center">
            <p className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary-400">
              {content.badge}
            </p>
            <h1 className="text-3xl font-black tracking-tight md:text-5xl">{content.h1}</h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-gray-300">{content.intro}</p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-3 text-sm font-bold text-white transition hover:bg-primary-400"
            >
              Demander un devis gratuit
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <ul className="mb-16 grid gap-3 sm:grid-cols-2">
          {content.highlights.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary-500" />
              {item}
            </li>
          ))}
        </ul>

        <AboutStudioSection showLists={false} />

        <div className="sr-only" aria-hidden="true">
          <div className="space-y-12">
            {content.sections.map((section) => (
              <section key={section.title}>
                <h2 className="mb-4 text-2xl font-black tracking-tight text-gray-950">{section.title}</h2>
                <div className="space-y-4 text-base leading-relaxed text-gray-600">
                  {section.body.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        {/* Dynamic Offers Section */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-black text-gray-900">Nos Offres & Packs</h2>
            <Link to="/contact" className="text-primary-600 font-bold text-sm hover:underline">Demander un devis sur mesure</Link>
          </div>
          
          {loadingOffers ? (
            <div className="grid gap-6 sm:grid-cols-2">
              {[1, 2].map(i => <div key={i} className="h-64 bg-gray-50 animate-pulse rounded-3xl" />)}
            </div>
          ) : filteredOffers.length > 0 ? (
            <div className="grid gap-8 sm:grid-cols-2">
              {filteredOffers.map((offer: ServiceOffer) => (
                <div key={offer.id} className="group relative flex flex-col rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-xl">
                  {offer.isPack && (
                    <span className="absolute -top-3 left-6 rounded-full bg-primary-500 px-4 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-lg">
                      Pack Premium
                    </span>
                  )}
                  <div className="mb-6 aspect-video overflow-hidden rounded-2xl bg-gray-50">
                    {offer.imageUrl ? (
                      <img src={offer.imageUrl} alt={offer.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-200">
                        <Camera className="h-12 w-12" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-xl font-black text-gray-900">{offer.title}</h3>
                      <span className="text-lg font-black text-primary-600">
                        {offer.price ? `${offer.price} DT` : 'Sur devis'}
                      </span>
                    </div>
                    <p className="mb-6 text-sm leading-relaxed text-gray-500">
                      {offer.description || 'Prestation professionnelle réalisée par nos experts.'}
                    </p>
                    
                    {/* Hidden SEO Content for the Pack/Offer */}
                    <div className="sr-only" aria-hidden="true">
                      <p>Pack {offer.title} - Expertise L Benna Production. {offer.description}</p>
                      <p>Mots-clés : {content.h1}, {content.badge}, services photo Tunisie, {offer.title} prix Tunisie.</p>
                      <p>#LBennaProduction #Service{offer.title.replace(/\s+/g, '')} #PhotographeTunisie #VideasteTunisie</p>
                    </div>

                    <ul className="mb-8 space-y-2">
                      {(offer.features || []).slice(0, 4).map((f: string, i: number) => (
                        <li key={i} className="flex items-center gap-2 text-xs font-bold text-gray-600">
                          <CheckCircle2 className="h-4 w-4 text-primary-500" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2 rounded-xl bg-gray-900 py-4 text-xs font-black uppercase tracking-widest text-white transition hover:bg-primary-600"
                  >
                    Réserver cet offre
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border-2 border-dashed border-gray-100 py-12 text-center">
              <p className="text-sm font-bold text-gray-400">Consultez-nous pour découvrir nos offres personnalisées.</p>
            </div>
          )}
        </section>

        <section className="mt-20 border-t border-gray-100 pt-16">
          <h2 className="mb-8 text-2xl font-black text-gray-900 px-4">Questions fréquentes</h2>
          <div className="space-y-4">
            {content.faq.map((item) => (
              <details
                key={item.q}
                className="group rounded-2xl border border-gray-100 bg-gray-50 transition open:bg-white open:shadow-md mx-4"
              >
                <summary className="cursor-pointer list-none px-6 py-4 font-bold text-gray-900 marker:content-none">
                  {item.q}
                </summary>
                <p className="border-t border-gray-100 px-6 pb-4 pt-2 leading-relaxed text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <h2 className="mb-6 text-center text-sm font-black uppercase tracking-[0.2em] text-gray-400">
            Services associés
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {related.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-800 shadow-sm transition hover:border-primary-300 hover:text-primary-600"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </section>

        {!settingsLoading && phone && (
          <section className="mt-16 overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-gray-950 via-gray-900 to-primary-950 p-8 text-white shadow-2xl md:p-10">
            <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary-500 shadow-lg">
                <Phone className="h-8 w-8" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-primary-300">Réponse sous 24 h</p>
                <h2 className="mt-1 text-2xl font-black tracking-tight">Parlons de votre projet</h2>
                <p className="mt-2 text-gray-400">Téléphone ou WhatsApp — équipe L Benna Production.</p>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <a
                  href={`tel:${phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-gray-950 transition hover:bg-gray-100"
                >
                  <Phone className="h-4 w-4" />
                  {phone}
                </a>
                {wa && (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3 text-sm font-bold text-white transition hover:bg-green-600"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        <div className="mt-16 rounded-3xl bg-gray-950 px-8 py-10 text-center text-white">
          <h2 className="text-2xl font-black">Prêt à lancer votre projet ?</h2>
          <p className="mx-auto mt-3 max-w-xl text-gray-400">
            L Benna Production vous répond sous 24 h — mariage, photobooth ou corporate à Tunis et Djerba.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="rounded-full bg-primary-500 px-8 py-3 text-sm font-bold transition hover:bg-primary-400"
            >
              Nous contacter
            </Link>
            <Link
              to={portfolioCtaHref ?? '/portfolio'}
              className="rounded-full border border-white/20 px-8 py-3 text-sm font-bold transition hover:bg-white/10"
            >
              Voir nos réalisations
            </Link>
          </div>
        </div>
      </div>

    </article>
  );
}
